import {
  IconButton,
  InputAdornment,
  ListItemText,
  Paper,
  Box,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Dispatch, SetStateAction, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from 'lib/theme'
import { useTranslation } from 'react-i18next'
import {
  OrderSearchIcon,
  SearchField,
  SearchMenuItem,
} from 'components/OrderSearchBar/OrderSearchBar.styled'

type ProductsSearchBarProps = {
  placeholderText: string
  handleClick: (key: string, query: string) => void
  ariaLabel: string
  input: string
  setInput: Dispatch<SetStateAction<string>>
  width: string
}
export const ProductsSearchBar = ({
  placeholderText,
  handleClick,
  ariaLabel,
  input,
  setInput,
  width,
}: ProductsSearchBarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [focus, setFocus] = useState(false)
  const [hover, setHover] = useState(false)
  const [iconHover, setIconHover] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const menuOptions = [
    {
      key: 'gtin',
      title: t('searchContent.gtin'),
    },
    {
      key: 'ext_id',
      title: t('searchContent.extId'),
    },
  ]

  const clearSearch = () => {
    setInput('')
    navigate('/product')
  }
  const handleSearch = (key: string) => {
    handleClick(key, input)
    setFocus(true)
  }
  return (
    <Box height={theme.spacing(8)}>
      <SearchField
        autoFocus={Boolean(input)}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{ '&.MuiFormControl-root': { width } }}
        onFocus={() => {
          setFocus(true)
          setErrorMessage('')
        }}
        onBlur={() => setFocus(false)}
        backgroundColor={
          hover ? theme.palette.action.hover : theme.palette.secondary.main
        }
        value={input}
        id='outlined-basic'
        placeholder={placeholderText}
        helperText={
          input.length < 2 && focus ? t('enterCharacters') : errorMessage
        }
        onChange={(e) => setInput(e.target.value)}
        SelectProps={{
          renderValue: (option: unknown) => option as { option: string },
        }}
        InputProps={
          focus
            ? {
                endAdornment: (
                  <InputAdornment position='end'>
                    {input && (
                      <IconButton
                        onMouseDown={clearSearch}
                        aria-label={t('clearSearch')}
                        sx={{
                          width: '52px',
                          height: '38px',
                          '&:hover': {
                            background: 'transparent',
                            color: theme.palette.action.hover,
                          },
                        }}>
                        <CloseIcon
                          sx={{
                            width: '20px',
                            height: '20px',
                            '&:hover': {
                              cursor: 'pointer',
                              background: 'transparent',
                            },
                          }}
                        />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }
            : {
                startAdornment: (
                  <InputAdornment position='start'>
                    <OrderSearchIcon aria-hidden hover={hover} />
                  </InputAdornment>
                ),
              }
        }
      />
      {input.length >= 2 && (
        <Paper
          sx={{
            borderRadius: 0,
            boxShadow: '0px 4px 6px 0px #cccccc',
            position: 'absolute',
            zIndex: 9,
            width,
          }}>
          {menuOptions.map((option) => (
            <div key={option.key}>
              <SearchMenuItem
                aria-hidden
                onMouseOver={() => setIconHover(option.key)}
                onMouseLeave={() => setIconHover('')}
                iconHover={iconHover === option.key}
                value={input}
                aria-label={ariaLabel}
                width={width}
                onClick={() => handleSearch(option.key)}>
                <ListItemText>
                  <b>{option.title}</b> {input}
                </ListItemText>
                <OrderSearchIcon aria-hidden hover={iconHover === option.key} />
              </SearchMenuItem>
            </div>
          ))}
        </Paper>
      )}
    </Box>
  )
}
